import { getUseInsurancePolicyPackageQkey } from '@parties/src/features/detail/api/insurance-policies/getInsurancePolicyPackageById';
import { useMutation, useQueryClient } from 'react-query';
import { IdResponse } from '../../../../../../../types/API';
import { wgApi } from '../../../../../../app/src/features/api/wgApi';
import {
  getUseInsurancePoliciesQKey,
  InsurencePoliciesPartialResponse,
} from './getInsurancePoliciesByParty';
import {
  getUseInsurancePolicyPackagesQKey,
  InsurencePolicyPackagesPartialResponse,
} from './getInsurancePolicyPackagesByParty';

interface Params {
  partyId: string;
  policyId: string;
  packageId?: string;
}

const deleteInsurancePolicy = async ({ partyId, policyId }: Params) => {
  const { data, isError } = await wgApi.delete<IdResponse>(
    `/v1/api/parties/${partyId}/insurance-policies/${policyId}`,
  );
  if (isError) throw new Error();
  return data;
};

export const useDeleteInsurancePolicy = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteInsurancePolicy, {
    onMutate: async ({ partyId, policyId, packageId }) => {
      const insurancePoliciesQKey = getUseInsurancePoliciesQKey({ partyId });
      const insurancePackagesQkey = getUseInsurancePolicyPackagesQKey({
        partyId,
      });

      await queryClient.cancelQueries(insurancePoliciesQKey);
      await queryClient.cancelQueries(insurancePackagesQkey);

      const previousPolicies = queryClient.getQueryData(insurancePoliciesQKey);
      const previousPackages = queryClient.getQueryData(insurancePackagesQkey);

      queryClient.setQueryData(
        insurancePoliciesQKey,
        (prev: InsurencePoliciesPartialResponse | undefined) => {
          const index =
            prev?.items.findIndex(({ id }) => id === policyId) || -1;
          if (index > -1) {
            const newPolicies = [...(prev?.items || [])];
            newPolicies.splice(index, 1);
            return {
              ...prev,
              items: newPolicies,
            };
          }
          return prev as InsurencePoliciesPartialResponse;
        },
      );

      if (packageId) {
        queryClient.setQueryData(
          insurancePackagesQkey,
          (prev: InsurencePolicyPackagesPartialResponse | undefined) => {
            const packageObj = prev?.items.find(({ id }) => id === packageId);
            if (packageObj) {
              const newPackages = (prev?.items || []).map((pack) => {
                if (pack.id === packageId) {
                  const newPolicies = (pack.insurance_policies || []).filter(
                    ({ id }) => id !== policyId,
                  );
                  return { ...pack, insurance_policies: newPolicies };
                }
                return pack;
              });
              return {
                ...prev,
                items: newPackages,
              };
            }
            return prev as InsurencePolicyPackagesPartialResponse;
          },
        );
      }

      return { previousPolicies, previousPackages };
    },
    onError: (_, { partyId, packageId }, ctx) => {
      const insurancePoliciesQKey = getUseInsurancePoliciesQKey({ partyId });
      const insurancePackagesQkey = getUseInsurancePolicyPackagesQKey({
        partyId,
      });
      queryClient.setQueryData(insurancePoliciesQKey, ctx?.previousPolicies);
      if (packageId) {
        queryClient.setQueryData(insurancePackagesQkey, ctx?.previousPackages);
      }
    },
    onSuccess: (_, { partyId, packageId }) => {
      queryClient.invalidateQueries(getUseInsurancePoliciesQKey({ partyId }));
      if (packageId) {
        queryClient.invalidateQueries(
          getUseInsurancePolicyPackagesQKey({ partyId }),
        );
        queryClient.invalidateQueries(
          getUseInsurancePolicyPackageQkey({
            partyId,
            packageId: packageId,
          }),
        );
      }
    },
  });
};
